import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';

import { loadApp } from '@/lib/loadApp';
import { About } from './About';
import { ClosingCta } from './ClosingCta';
import { PAGE_TITLE } from './content';
import { LANDING_COPY, readLandingLang, saveLandingLang, type LandingLang } from './copy';
import { Faq } from './Faq';
import { Footer } from './Footer';
import { Header } from './Header';
import { HeroIllustration } from './HeroIllustration';
import { HowItWorks } from './HowItWorks';
import { Pricing } from './Pricing';
import { Screenshots } from './Screenshots';
import { UnderTheHood } from './UnderTheHood';
import { Why } from './Why';
import './landing.css';

/** Best-effort: if it fails, the route's own load (loadAppForRoute) recovers when it's needed. */
const prefetchApp = () => void loadApp().catch(() => undefined);

/** Warm the app chunk on hover/focus/touch, so Login opens without a wait. */
const prefetchOnIntent = {
  onMouseEnter: prefetchApp,
  onFocus: prefetchApp,
  onTouchStart: prefetchApp,
};

/** Skip the idle-time prefetch on metered or very slow connections — intent still triggers it. */
const canPrefetchOnIdle = (): boolean => {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType ?? '');
};

/**
 * Public page at "/". Deliberately built without antd or Redux — plain
 * markup and one stylesheet — so it loads fast; the signed-in app is a
 * separate chunk (see lib/loadApp.ts).
 */
export const LandingPage = () => {
  const navigationType = useNavigationType();
  const { key, hash } = useLocation();
  const [lang, setLang] = useState<LandingLang>(readLandingLang);
  const copy = LANDING_COPY[lang];
  const switchLang = () => {
    const next: LandingLang = lang === 'hi' ? 'en' : 'hi';
    setLang(next);
    saveLandingLang(next);
  };

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  // Screen readers and the browser's font choice follow the page's language; give it back on leaving.
  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = copy.htmlLang;
    return () => {
      document.documentElement.lang = previous;
    };
  }, [copy.htmlLang]);

  // react-router keeps the window's scroll offset across routes, so arriving from the app
  // (Exit demo, Logout, an expired session) opened this long page wherever the app page had
  // been scrolled, even at the very bottom. Start at the top, before the first paint. A
  // back/forward or a fresh load (POP) keeps the browser's own restoration, and so does a
  // #section link.
  useLayoutEffect(() => {
    if (navigationType === 'POP') return;
    // A link like /#faq from the login page opens at that section.
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    // A folded section (the developer one) opens first, so there's something to land on.
    if (target instanceof HTMLDetailsElement) target.open = true;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [key, hash, navigationType]);

  useEffect(() => {
    if (!canPrefetchOnIdle()) return;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(prefetchApp);
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(prefetchApp, 2_000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="lp">
      <Header copy={copy} lang={lang} onSwitchLang={switchLang} linkProps={prefetchOnIntent} />

      <main>
        <section className="lp-hero">
          <div className="lp-container lp-hero__inner">
            <div className="lp-hero__copy">
              <p className="lp-eyebrow">{copy.hero.eyebrow}</p>
              <h1>{copy.hero.title}</h1>
              <p className="lp-lead">{copy.hero.lead}</p>
              <div className="lp-actions">
                <Link to="/demo" className="lp-btn lp-btn--primary" {...prefetchOnIntent}>
                  {copy.cta.demo}
                </Link>
                <Link to="/signup" className="lp-btn lp-btn--ghost" {...prefetchOnIntent}>
                  {copy.cta.signup}
                </Link>
              </div>
              <p className="lp-hero__note">{copy.hero.note}</p>
            </div>
            <HeroIllustration caption={copy.hero.sample} />
          </div>
        </section>
        <Why who={copy.who} why={copy.why} />
        <HowItWorks copy={copy.how} />
        <Screenshots />
        <Pricing copy={copy.pricing} signup={copy.cta.signup} linkProps={prefetchOnIntent} />
        <Faq copy={copy.faq} />
        <About copy={copy.about} />
        <UnderTheHood summary={copy.hood.summary} />
        <ClosingCta copy={copy.closing} signup={copy.cta.signup} linkProps={prefetchOnIntent} />
      </main>

      <Footer copy={copy} linkProps={prefetchOnIntent} />
    </div>
  );
};
